import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PublicLayout from '../components/PublicLayout';
import { Check, ArrowRight, Sparkles, Building2 } from 'lucide-react';

const PricingPage = () => {
  const commonFeatures = [
    'Αυτόματος Έλεγχος Δικαιολογητικών',
    'Priority Support',
    'Δημόσια Links Ανεβάσματος',
    'Ομαδική Συνεργασία',
    'Αυτόματες Υπενθυμίσεις',
  ];

  const plans = [
    {
      name: 'Trial',
      price: 'Δωρεάν',
      period: '1 μήνας',
      description: 'Για να δοκιμάσετε την πλατφόρμα',
      limits: '2 Έργα Αίτησης + 2 Έργα Υλοποίησης',
      features: commonFeatures,
      highlight: false,
      cta: 'Ξεκινήστε Δωρεάν'
    },
    {
      name: 'Basic',
      price: '250€',
      period: '/ 6 μήνες',
      description: 'Για μικρούς συμβούλους',
      limits: '12 Έργα Αίτησης + 12 Έργα Υλοποίησης',
      features: commonFeatures,
      highlight: false,
      cta: 'Επιλογή Basic'
    },
    {
      name: 'Professional',
      price: '450€',
      period: '/ 6 μήνες',
      description: 'Για επαγγελματίες',
      limits: '30 Έργα Αίτησης + 30 Έργα Υλοποίησης',
      features: commonFeatures,
      highlight: true,
      cta: 'Επιλογή Professional'
    },
  ];

  const faqs = [
    {
      question: 'Πώς λειτουργεί το Trial;',
      answer: 'Το Trial διαρκεί 1 μήνα και σας δίνει πλήρη πρόσβαση σε όλες τις λειτουργίες με 2 έργα αίτησης και 2 έργα υλοποίησης.'
    },
    {
      question: 'Μπορώ να αλλάξω πακέτο;',
      answer: 'Ναι, μπορείτε να αναβαθμίσετε ή να υποβαθμίσετε το πακέτο σας οποιαδήποτε στιγμή επικοινωνώντας μαζί μας.'
    },
    {
      question: 'Τι περιλαμβάνει ο αυτόματος έλεγχος;',
      answer: 'Ο αυτόματος έλεγχος αναλύει τα δικαιολογητικά σας για λάθη, ελλείψεις και συμμόρφωση με κάποιες από τις γενικές απαιτήσεις των προγραμμάτων.'
    },
  ];

  return (
    <PublicLayout>
      {/* Header */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 rounded-full border border-cyan-200 mb-6">
            <Sparkles className="w-4 h-4 text-cyan-600" />
            <span className="text-sm font-medium text-cyan-700">1 μήνας δωρεάν</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Τα πακέτα μας
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Επιλέξτε το πακέτο που ταιριάζει στις ανάγκες σας. 
            Ξεκινήστε δωρεάν και αναβαθμίστε όποτε θέλετε.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {/* Introductory pricing note */}
          <p className="text-center text-xs text-slate-400 mb-8">
            * τιμή γνωριμίας πρώτου έτους λειτουργίας
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative p-8 rounded-3xl border-2 transition-all duration-300 hover:-translate-y-2 ${
                  plan.highlight 
                    ? 'border-cyan-500 bg-gradient-to-b from-cyan-50 to-white shadow-xl shadow-cyan-500/10' 
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg'
                }`}
              >
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-medium rounded-full">
                      Δημοφιλές
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {plan.name}
                  </h3>
                  <p className="text-sm text-slate-500">{plan.description}</p>
                </div>

                <div className="mb-4">
                  <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-slate-500 ml-1">{plan.period}</span>
                  )}
                </div>

                {/* Project Limits */}
                <div className="mb-6 p-3 bg-slate-100 rounded-xl">
                  <p className="text-sm font-semibold text-slate-800 text-center">{plan.limits}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-cyan-600' : 'text-emerald-500'}`} />
                      <span className="text-slate-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/login">
                  <Button
                    className={`w-full rounded-full py-6 font-medium transition-all hover:scale-105 ${
                      plan.highlight
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {plan.cta}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Supported */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Υποστηριζόμενα Προγράμματα
          </h2>
          <div className="flex flex-wrap justify-center gap-8">
            {['ΕΣΠΑ', 'ΟΑΕΔ', 'Αναπτυξιακός Νόμος'].map((program, index) => (
              <div key={index} className="flex items-center gap-3 px-6 py-4 bg-white rounded-xl border border-slate-200">
                <Building2 className="w-6 h-6 text-cyan-600" />
                <span className="font-medium text-slate-900">{program}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Συχνές Ερωτήσεις
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="p-6 bg-slate-50 rounded-2xl">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Έχετε ερωτήσεις;
          </h2>
          <p className="text-slate-400 mb-8">
            Επικοινωνήστε μαζί μας για να συζητήσουμε τις ανάγκες σας.
          </p>
          <Link to="/contact">
            <Button className="rounded-full px-8 py-6 bg-white text-slate-900 hover:bg-slate-100 font-medium hover:scale-105 transition-all">
              Επικοινωνία
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
};

export default PricingPage;
